import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { trpc } from "../../../../api/trpc/trpc"
import { getCurrency } from "../../../../i18n/config"
import { LegendItem } from "../../../../components/chart/ChartLegend"
import { ProdAndConsGroup, DistributionTariff, EnergyUnit } from "@nano-portal/shared"

type GrouppedDataInTime = Map<ProdAndConsGroup, Map<number, number>>
const prepareChartData = (
  group: ProdAndConsGroup,
  data: {
    consumptionsInTime?: GrouppedDataInTime
    pricesInTime?: GrouppedDataInTime
    productionsInTime?: GrouppedDataInTime
    installedPower: number
    annualConsumption: number
  }
) => {
  const { consumptionsInTime, pricesInTime, productionsInTime, installedPower, annualConsumption } = data
  if (!consumptionsInTime || !pricesInTime || !productionsInTime) {
    return []
  }

  const groupPricesInTime = pricesInTime.get(group)
  if (!groupPricesInTime) {
    return []
  }

  const result: { marketPrice: number; consumption: number; production: number; timeUnit: number }[] = []
  for (const [time, averagePrice] of groupPricesInTime) {
    const averageConsumption = consumptionsInTime.get(group)?.get(time) ?? 0
    const averageProduction = productionsInTime.get(group)?.get(time) ?? 0

    result.push({
      marketPrice: averagePrice,
      consumption: averageConsumption * annualConsumption,
      production: averageProduction * installedPower,
      timeUnit: time,
    })
  }

  return result
}

export const useProductionAndConsumptionController = () => {
  const { t } = useTranslation("microsite")

  const [filter, setFilter] = useState({ spotPrices: true, production: true, consumption: true })
  const [annualConsumption, setAnnualConsumption] = useState<number | undefined>(2)
  const [installedPower, setInstalledPower] = useState<number | undefined>(1)
  const [distributionTariff, setDistributionTariff] = useState(DistributionTariff.D01d)
  const [optimization, setOptimization] = useState(0)
  const [group, setGroup] = useState(ProdAndConsGroup.Months)

  const pricesQuery = trpc.useQuery(["web.allPrices"])
  const consumptionsQuery = trpc.useQuery(["web.allConsumptions", { distributionTariff, optimization }], {
    keepPreviousData: true,
  })
  const productionsQuery = trpc.useQuery(["web.allProductions"])

  const legendItems: LegendItem[] = useMemo(
    () => [
      {
        color: "primary",
        label: t("market_price_with_unit", {
          currency: getCurrency().symbol,
          unit: EnergyUnit.kWh,
        }),
        hidden: !filter.spotPrices,
        onClick: () => setFilter((prev) => ({ ...prev, spotPrices: !prev.spotPrices })),
      },
      {
        color: "successes",
        label: t("daily_price_household_consumption", { unit: EnergyUnit.kWh }),
        hidden: !filter.consumption,
        onClick: () => setFilter((prev) => ({ ...prev, consumption: !prev.consumption })),
      },
      {
        color: "warning",
        label: t("daily_price_household_production", { unit: EnergyUnit.kWh }),
        hidden: !filter.production,
        onClick: () => setFilter((prev) => ({ ...prev, production: !prev.production })),
      },
    ],
    [t, filter]
  )

  const toggleButtonItems = useMemo(
    () => [
      { title: t("months"), value: ProdAndConsGroup.Months },
      { title: t("hours"), value: ProdAndConsGroup.Hours },
    ],
    [t]
  )

  const totalConsumption = (consumptionsQuery.data?.totalConsumptionValue ?? 0) * (annualConsumption ?? 0)
  const totalProduction = (productionsQuery.data?.totalProductionValue ?? 0) * (installedPower ?? 0)
  const settlement = Math.abs(totalConsumption - totalProduction)

  return {
    isError: productionsQuery.isError || consumptionsQuery.isError || pricesQuery.isError,
    isLoading: productionsQuery.isLoading || consumptionsQuery.isLoading || pricesQuery.isLoading,
    chartData: prepareChartData(group, {
      consumptionsInTime: consumptionsQuery.data?.consumptionsInTime,
      pricesInTime: pricesQuery.data,
      productionsInTime: productionsQuery.data?.productionsInTime,
      installedPower: installedPower ?? 0,
      annualConsumption: annualConsumption ?? 0,
    }),
    filter,
    legendItems,
    annualConsumption,
    setAnnualConsumption,
    distributionTariff,
    setDistributionTariff,
    installedPower,
    setInstalledPower,
    optimization,
    setOptimization,
    totalProduction,
    totalConsumption,
    settlement,
    toggleButtonItems,
    group,
    onGroupChange: (group: ProdAndConsGroup) => group && setGroup(group),
  }
}
